import React, { useState, useEffect, useRef } from 'react';
import { Search, QrCode, Receipt, X, ChevronDown, Check } from 'lucide-react';
import { Input } from '../ui/input';
import { adminQrRedemptionsAPI, adminStoresAPI } from '../../utils/api';
import { toast } from '../../hooks/use-toast';
import { formatDateTimeLocal, formatDateLocal } from '../../utils/dateFormat';
import { Toaster } from '../ui/toaster';

/* Los montos llegan como número; si faltan —canjes anteriores a que se
   capturara el monto— se dice con un guion en vez de enseñar $0. */
const pesos = (n) => {
  const v = Number(n);
  return Number.isFinite(v) && n !== null && n !== '' ? `$${v.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';
};

const QrRedemptionsManagement = () => {
  const [redemptions, setRedemptions] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [storeSelectOpen, setStoreSelectOpen] = useState(false);
  const storeSelectRef = useRef(null);
  const [filters, setFilters] = useState({
    email: '',
    ticket_number: '',
    date_from: '',
    date_to: '',
    store_ids: [],
  });
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, per_page: 15, total: 0 });

  useEffect(() => { loadStores(); }, []);
  useEffect(() => { loadRedemptions(); }, [filters]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (storeSelectRef.current && !storeSelectRef.current.contains(e.target)) {
        setStoreSelectOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadStores = async () => {
    try {
      const response = await adminStoresAPI.getAll();
      setStores(response.data || []);
    } catch { setStores([]); }
  };

  const loadRedemptions = async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        page,
        per_page: 15,
        ...(filters.email && { email: filters.email }),
        ...(filters.ticket_number && { ticket_number: filters.ticket_number }),
        ...(filters.date_from && { date_from: filters.date_from }),
        ...(filters.date_to && { date_to: filters.date_to }),
        ...(filters.store_ids?.length > 0 && { store_ids: filters.store_ids }),
      };
      const response = await adminQrRedemptionsAPI.getAll(params);
      setRedemptions(response.data);
      if (response.pagination) setPagination(response.pagination);
    } catch (error) {
      toast({ title: "Error", description: error.message || "No se pudieron cargar los canjes", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <Toaster />
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Canjes de QR</h2>
        <p className="text-gray-500">Vista de canjes de QR y tickets de compra (solo lectura)</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="relative" ref={storeSelectRef}>
          <label className="block text-xs text-gray-500 mb-1">Por tienda</label>
          <div className="flex items-center gap-1 rounded-lg bg-white border border-gray-300 min-h-10 px-3 py-2">
            <button
              type="button"
              onClick={() => setStoreSelectOpen((o) => !o)}
              className="flex-1 flex flex-wrap items-center gap-1.5 min-w-0 text-left"
            >
              {filters.store_ids.length === 0 ? (
                <span className="text-gray-400 text-sm">Todas las tiendas</span>
              ) : (
                filters.store_ids.map((id) => {
                  const store = stores.find((s) => String(s.id) === String(id));
                  return store ? (
                    <span key={store.id} className="inline-flex items-center px-2 py-0.5 rounded bg-gray-200 text-gray-800 text-xs">
                      {store.name}
                    </span>
                  ) : null;
                })
              )}
            </button>
            {filters.store_ids.length > 0 && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setFilters((f) => ({ ...f, store_ids: [] })); setStoreSelectOpen(false); }}
                className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                title="Quitar todas las tiendas"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${storeSelectOpen ? 'rotate-180' : ''}`} />
          </div>
          {storeSelectOpen && (
            <div className="absolute z-20 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg py-1 max-h-56 overflow-y-auto">
              {stores.map((store) => {
                const isSelected = filters.store_ids.includes(String(store.id));
                return (
                  <button
                    key={store.id}
                    type="button"
                    onClick={() => {
                      setFilters((f) => ({
                        ...f,
                        store_ids: isSelected
                          ? f.store_ids.filter((id) => id !== String(store.id))
                          : [...f.store_ids, String(store.id)],
                      }));
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 text-gray-900"
                  >
                    <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-gray-200 border-gray-700' : 'border-gray-300'}`}>
                      {isSelected && <Check className="w-3 h-3" />}
                    </span>
                    {store.name}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div>
          <Input type="text" placeholder="Buscar por email..." value={filters.email} onChange={(e) => setFilters({ ...filters, email: e.target.value })} className="bg-gray-50 border-gray-300 text-gray-900" />
        </div>
        <div>
          <Input type="text" placeholder="Buscar por ticket..." value={filters.ticket_number} onChange={(e) => setFilters({ ...filters, ticket_number: e.target.value })} className="bg-gray-50 border-gray-300 text-gray-900" />
        </div>
        <div>
          <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="bg-gray-50 border-gray-300 text-gray-900" />
        </div>
        <div>
          <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="bg-gray-50 border-gray-300 text-gray-900" />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Cargando...</div>
      ) : redemptions.length === 0 ? (
        <div className="text-center py-12 text-gray-500">No hay canjes registrados</div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1040px]">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Suscriptor</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">QR / Promoción</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Ticket</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Total</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Se ahorró</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Fecha canje</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {redemptions.map((redemption) => (
                  <tr key={redemption.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-gray-900 font-medium">{redemption.user?.name || '—'}</div>
                        <div className="text-gray-500 text-sm">{redemption.user?.email || ''}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <QrCode className="w-4 h-4 text-gray-400" />
                        <div>
                          <div className="text-gray-900 text-sm">{redemption.promotion_name || redemption.qr_code}</div>
                          {redemption.store && <div className="text-gray-500 text-xs">{redemption.store.name}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {redemption.ticket.number ? (
                        <div className="flex items-center gap-2">
                          <Receipt className="w-4 h-4 text-gray-400" />
                          <div>
                            <div className="text-gray-900 text-sm">#{redemption.ticket.number}</div>
                            {redemption.ticket.date && <div className="text-gray-500 text-xs">{formatDateLocal(redemption.ticket.date)}</div>}
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-gray-900">{pesos(redemption.ticket.total)}</td>
                    <td className="px-6 py-4 font-medium text-gray-900">{pesos(redemption.saved)}</td>
                    <td className="px-6 py-4 text-gray-600">
                      {formatDateTimeLocal(redemption.redeemed_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {pagination.last_page > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-gray-500 text-sm">
            Mostrando {((pagination.current_page - 1) * pagination.per_page) + 1} a {Math.min(pagination.current_page * pagination.per_page, pagination.total)} de {pagination.total}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => loadRedemptions(pagination.current_page - 1)}
              disabled={pagination.current_page === 1}
              className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
            >
              Anterior
            </button>
            <button
              onClick={() => loadRedemptions(pagination.current_page + 1)}
              disabled={pagination.current_page === pagination.last_page}
              className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
            >
              Siguiente
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default QrRedemptionsManagement;
